import React, { useState } from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { ItemSourceSentenceTranslationValidatorItemStatus as ItemStatus } from 'constants/itemSourceSentenceTranslationValidatorStatus';
import ItemSourceSentenceTranslationValidatorItemsModel from 'models/ItemSourceSentenceTranslationValidatorItemsModel';
import ModuleContext from 'CMFW/context/ModuleContext';
import { IItemSourceSentenceTranslationValidatorItem } from '../../../types/itemSourceSentenceTranslationValidator';
import { useDispatch } from 'react-redux';
import { tabsOpened } from 'store/actions/tabs';
import { IRefIds } from 'constants/refs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAlignLeft, faFileAlt } from '@fortawesome/free-solid-svg-icons';

const ItemSourceSentenceTranslationValidatorActionRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as IItemSourceSentenceTranslationValidatorItem;
  const itemsModel = React.useContext(ModuleContext) as ItemSourceSentenceTranslationValidatorItemsModel;
  const dispatch = useDispatch();
  const [processing, setProcessing] = useState<'keep' | 'change' | 'markDone' | null>(null);

  if (!data) return null;
  if (data.status === ItemStatus.PENDING) return null;

  // If there's an error, show nothing in the action renderer
  if (data.error_message || data.ai_result?.decision === 'error') {
    return <ActionsContainer />;
  }

  const isKeep = data.decision === 'keep';
  const isChange = data.decision === 'change';
  const isMarkDone = data.decision === 'cannot_modify';
  const isDone = data.status === ItemStatus.DONE;
  const hasSelection = isDone;
  const cannotModify = data.ai_result?.can_modify === false;
  const canModify = !cannotModify;
  const hasModifiedTranslation =
    canModify && data.ai_result?.modified_translation && data.ai_result.modified_translation.trim() !== '';
  const showMarkDoneAction = cannotModify;

  const handleKeep = async () => {
    if (processing || (isKeep && isDone)) return;
    setProcessing('keep');
    try {
      await itemsModel.keepItem(data.id);
    } finally {
      setProcessing(null);
    }
  };
  const handleChange = async () => {
    if (processing || (isChange && isDone)) return;
    setProcessing('change');
    try {
      await itemsModel.changeItem(data.id);
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkDone = async () => {
    if (processing || (isMarkDone && isDone)) return;
    setProcessing('markDone');
    try {
      await itemsModel.markItemAsDone(data.id);
    } finally {
      setProcessing(null);
    }
  };

  const handleViewSentences = () => {
    if (data.sentences_ids && data.sentences_ids.length > 0 && data.navigation_data) {
      const sentencesSearch = data.sentences_ids.join(',');
      dispatch(
        tabsOpened({
          refId: IRefIds.sentences,
          search: sentencesSearch,
          courseId: data.navigation_data.courseId,
        }),
      );
    }
  };

  const handleViewPanels = () => {
    if (data.panel_ids && data.panel_ids.length > 0 && data.navigation_data) {
      const panelsSearch = data.panel_ids.join(',');
      dispatch(
        tabsOpened({
          refId: IRefIds.panels,
          search: panelsSearch,
          courseId: data.navigation_data.courseId,
        }),
      );
    }
  };

  return (
    <ActionsContainer>
      <ButtonsRow>
        <ActionButton
          onClick={handleKeep}
          disabled={processing !== null}
          $selected={isKeep && isDone}
          $hasSelection={hasSelection}
          $variant="keep"
          data-selected={isKeep && isDone ? 'true' : 'false'}
        >
          {processing === 'keep' ? <i className="fa fa-spinner fa-spin" /> : 'Keep'}
        </ActionButton>
        {hasModifiedTranslation && (
          <ActionButton
            onClick={handleChange}
            disabled={processing !== null}
            $selected={isChange && isDone}
            $hasSelection={hasSelection}
            $variant="change"
            data-selected={isChange && isDone ? 'true' : 'false'}
          >
            {processing === 'change' ? <i className="fa fa-spinner fa-spin" /> : 'Change'}
          </ActionButton>
        )}
        {showMarkDoneAction && (
          <ActionButton
            onClick={handleMarkDone}
            disabled={processing !== null}
            $selected={isMarkDone && isDone}
            $hasSelection={hasSelection}
            $variant="accept"
            data-selected={isMarkDone && isDone ? 'true' : 'false'}
          >
            {processing === 'markDone' ? <i className="fa fa-spinner fa-spin" /> : 'Mark as Done'}
          </ActionButton>
        )}
      </ButtonsRow>
      {(data.sentences_ids && data.sentences_ids.length > 0) || (data.panel_ids && data.panel_ids.length > 0) ? (
        <ConceptRow>
          {data.sentences_ids && data.sentences_ids.length > 0 && (
            <ConceptButton onClick={handleViewSentences} title={`View ${data.sentences_ids.length} related sentences`}>
              <FontAwesomeIcon icon={faAlignLeft} />
            </ConceptButton>
          )}
          {data.panel_ids && data.panel_ids.length > 0 && (
            <ConceptButton onClick={handleViewPanels} title={`View ${data.panel_ids.length} related panels`}>
              <FontAwesomeIcon icon={faFileAlt} />
            </ConceptButton>
          )}
        </ConceptRow>
      ) : null}
    </ActionsContainer>
  );
};

export default ItemSourceSentenceTranslationValidatorActionRenderer;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 5px;
  justify-content: center;
  align-items: center;
`;

const ConceptRow = styled(ButtonsRow)`
  margin-top: 4px;
  justify-content: right;
  width: 100%;
`;

const ActionButton = styled.button<{
  $selected?: boolean;
  $variant?: 'keep' | 'change' | 'accept';
  $hasSelection?: boolean;
}>`
  padding: 6px 14px;
  background-color: ${(p) =>
    p.$selected
      ? p.$variant === 'keep'
        ? '#66BB6A'
        : p.$variant === 'change'
        ? '#42A5F5'
        : '#8E24AA'
      : p.$variant === 'keep'
      ? '#e8f5e9'
      : p.$variant === 'change'
      ? '#e3f2fd'
      : '#f3e5f5'};
  color: ${(p) =>
    p.$selected ? '#fff' : p.$variant === 'keep' ? '#2e7d32' : p.$variant === 'change' ? '#1565c0' : '#6a1b9a'};
  border: 1px solid
    ${(p) =>
      p.$selected
        ? 'transparent'
        : p.$variant === 'keep'
        ? '#81c784'
        : p.$variant === 'change'
        ? '#64b5f6'
        : '#ce93d8'};
  border-radius: 6px;
  cursor: ${(p) => (p.$selected ? 'default' : 'pointer')};
  font-weight: 600;
  transition: all 0.2s ease;
  opacity: ${(p) => (p.$selected ? 1 : p.$hasSelection ? 0.3 : 1)};
  min-width: 74px;
  text-align: center;
  pointer-events: ${(p) => (p.$selected ? 'none' : 'auto')};
  &:hover:not(:disabled):not([data-selected='true']) {
    opacity: 1;
    background-color: ${(p) => (p.$variant === 'keep' ? '#66BB6A' : p.$variant === 'change' ? '#42A5F5' : '#8E24AA')};
    color: #fff;
  }
  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

const ConceptButton = styled.button`
  padding: 6px 10px;
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 35px;

  &:hover {
    background-color: #1890ff;
    color: #fff;
    border-color: #1890ff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
